import React, { useState } from 'react';

import { CustomSelect } from '../../../../components';
import { EChallengesType, ECriterion, EPillar } from '../../../../types/challenges';
import CustomInput from './CustomInput';
import CustomSwitch from './CustomSwitch';
import CustomTextarea from './CustomTextarea';

interface Option {
  label: string;
  value: string;
}

type FormType = {
  name: string;
  description: string;
  criterion: ECriterion | '';
  criterionValue: number;
  criterionSecondValue: number;
  pillar: EPillar | '';
  variableId: number;
  points: number;
  uniqueExecute: boolean;
  active: boolean;
  validUntil: string;
  type: EChallengesType | '';
};

interface EditFormProps {
  form: FormType;
  onFormChange: (field: keyof FormType, value: any) => void;
  criterionOptions: Option[];
  pillarOptions: Option[];
  variableOptions: Option[];
  onBack: () => void;
  onSubmit: () => void;
}

function parseDateString(dateStr: string | null | undefined): Date | null {
  if (!dateStr) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return new Date(dateStr);
  const parts = dateStr.split('/');
  if (parts.length === 3) {
    const [day, month, year] = parts;
    return new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
  }
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? null : d;
}

const EditForm: React.FC<EditFormProps> = ({
  form,
  onFormChange,
  criterionOptions,
  pillarOptions,
  variableOptions,
  onBack,
  onSubmit
}) => {
  const [error, setError] = useState<string | null>(null);

  const validate = () => {
    if (!form.name.trim()) return 'Nome é obrigatório';
    if (!form.description.trim()) return 'Descrição é obrigatória';
    if (!form.criterion) return 'Critério é obrigatório';
    if (!form.pillar) return 'Pilar é obrigatório';
    if (!form.points || isNaN(form.points) || form.points <= 0) return 'Pontos devem ser maiores que zero';
    if (!form.validUntil) return 'Data de validade é obrigatória';
    return null;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    onSubmit();
  };

  const formatDateForInput = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };



  return (
    <form className="display-flex flex-column" onSubmit={handleSubmit}>
      <div className="display-flex flex-column width-100pct">
        <CustomInput
          label="Nome"
          id="challengeName"
          value={form.name}
          onChange={v => onFormChange('name', v)}
          maxLength={150}
          className="width-100pct margin-bottom-20"
        />
        <CustomTextarea
          label="Descrição"
          id="challengeDescription"
          value={form.description}
          onChange={v => onFormChange('description', v)}
          maxLength={250}
          rows={3}
          className="width-100pct margin-bottom-20"
        />


      </div>
      <div className="display-flex flex-row gap-16">
        <CustomInput
          label="Pontos"
          id="challengePoints"
          value={form.points}
          onChange={v => onFormChange('points', Number(v.replace(/[^0-9]/g, '')))}
          type="text"
          placeholder="Ex: 100, 200, 300"
          className="width-10pct"
        />
      </div>

      <div className="display-flex flex-column width-100pct">
        <div className="display-flex flex-row margin-top-10">
          <h3 className="text-size-18 text-bold margin-bottom-16">Configuração</h3>
        </div>
        <div className="display-flex flex-row margin-top-10 padding-left-10">
          <CustomSelect
            label="Critério"
            id="challengeCriterion"
            value={form.criterion}
            onChange={v => onFormChange('criterion', v)}
            options={criterionOptions}
            className="width-20pct"
          />
          <CustomInput
            label="Valor do Critério"
            id="challengeCriterionValue"
            value={form.criterionValue}
            onChange={v => onFormChange('criterionValue', Number(v.replace(/[^0-9]/g, '')))}
            type="text"
            className="margin-top-16 width-20pct margin-left-10"
          />
          {form.criterion === ECriterion.BETWEEN && (
            <CustomInput
              label="Segundo Valor do Critério"
              id="challengeCriterionSecondValue"
              value={form.criterionSecondValue}
              onChange={v => onFormChange('criterionSecondValue', Number(v.replace(/[^0-9]/g, '')))}
              type="text"
              className="margin-top-16 width-20pct margin-left-10"
            />
          )}
        </div>
        <div className="display-flex flex-row margin-top-10 padding-left-10">
          <CustomSelect
            label="Pilar"
            id="challengePillar"
            value={form.pillar}
            onChange={v => onFormChange('pillar', v)}
            options={pillarOptions}
            className="margin-top-16 width-20pct"
          />
          <CustomSelect
            label="Variável"
            id="variableId"
            value={form.variableId.toString()}
            onChange={v => onFormChange('variableId', Number(v.replace(/[^0-9]/g, '')))}
            options={variableOptions}
            className="margin-top-16 width-20pct margin-left-10"
          />
        </div>

      </div>

      <div className="display-flex flex-column width-100pct margin-top-10">
        <div className="display-flex flex-row width-100pct">
          <h3 className="text-size-18 text-bold margin-bottom-16">Configurações Avançadas</h3>
        </div>
        <div className="display-flex flex-row width-100pct padding-left-10 margin-top-10">
          <CustomSwitch
            label="Execução Única"
            checked={form.uniqueExecute}
            onChange={v => onFormChange('uniqueExecute', v)}
            className="margin-right-10"
          />
          <CustomSwitch
            label="Ativo"
            checked={form.active}
            onChange={v => onFormChange('active', v)}
            className="margin-right-10"
          />
        </div>

        <div className="display-flex flex-row width-100pct padding-left-10">
          <CustomInput
            label="Válido Até"
            id="challengeValidUntil"
            value={formatDateForInput(form.validUntil)}
            onChange={v => onFormChange('validUntil', v)}
            type="date"
            className="margin-top-20 width-200"
          />
        </div>
      </div>

      <div className="padding-y-20">
        {error && <div className="alert alert-danger">{error}</div>}
      </div>
      <div className="display-flex gap-10 justify-content-end margin-top-24">
        <button type="button" className="btn btn-default" onClick={onBack}>Voltar</button>
        <button type="submit" className="btn btn-primary">Salvar desafio</button>
      </div>
    </form>
  );
};

export { parseDateString };
export default EditForm; 